import { useEffect, useState } from "react";
import api from "@/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
         AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
         AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Users, PlusCircle, Pencil, Trash2, ShieldCheck } from "lucide-react";
import PermissionsModal from "@/components/PermissionsModal";

// Shared validators — mirror the backend regex exactly so users get instant,
// accurate feedback before submit.
const CUSTOM_ID_RE = /^[a-zA-Z0-9_]{3,20}$/;
const MOBILE_RE = /^\+?(?:91)?[6-9]\d{9}$/;
const CUSTOM_ID_ERR = "Custom ID must be 3-20 characters (letters, numbers, underscore only)";
const MOBILE_ERR = "Please enter a valid 10-digit mobile number (starting with 6-9)";

const validateCustomId = (id) => CUSTOM_ID_RE.test(id || "");
const validateMobile = (m) => MOBILE_RE.test((m || "").replace(/[^\d+]/g, ""));

const EMPTY_PERMS = {
  can_verify_gov_schemes: false,
  can_add_patients: false,
  can_view_queue: false,
  can_view_appointments: false,
};

const PERMISSION_LABELS = {
  can_verify_gov_schemes: "Verify RGHS / Ayushman cards",
  can_add_patients: "Add patients to a doctor's queue",
  can_view_queue: "View live queues",
  can_view_appointments: "View today's appointments",
};

function PermissionCheckboxes({ value, onChange, testidPrefix = "perm" }) {
  return (
    <div className="space-y-2">
      {Object.keys(PERMISSION_LABELS).map((key) => (
        <label key={key} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
            checked={!!value?.[key]}
            onChange={(e) => onChange({ ...value, [key]: e.target.checked })}
            data-testid={`${testidPrefix}-${key}`}
          />
          <span>{PERMISSION_LABELS[key]}</span>
        </label>
      ))}
    </div>
  );
}

function ReceptionistForm({ mode, initial, onSaved }) {
  const [customId, setCustomId] = useState(initial?.custom_id || "");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState(initial?.full_name || "");
  const [mobile, setMobile] = useState(initial?.mobile || "");
  const [permissions, setPermissions] = useState(initial?.permissions || EMPTY_PERMS);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const reset = () => {
    setCustomId(initial?.custom_id || "");
    setPassword("");
    setFullName(initial?.full_name || "");
    setMobile(initial?.mobile || "");
    setPermissions(initial?.permissions || EMPTY_PERMS);
  };

  const submit = async () => {
    setBusy(true);
    try {
      if (mode === "create") {
        if (!validateCustomId(customId)) {
          toast.error(CUSTOM_ID_ERR);
          setBusy(false);
          return;
        }
        if (password.length < 8) {
          toast.error("Password must be at least 8 characters");
          setBusy(false);
          return;
        }
        if (!validateMobile(mobile)) {
          toast.error(MOBILE_ERR);
          setBusy(false);
          return;
        }
        await api.post("/receptionist/register", {
          custom_id: customId, password, full_name: fullName,
          mobile, permissions,
        });
        toast.success("Receptionist created");
      } else {
        if (!validateMobile(mobile)) {
          toast.error(MOBILE_ERR);
          setBusy(false);
          return;
        }
        await api.put(`/receptionist/${initial.id}`, {
          full_name: fullName, mobile, permissions,
        });
        toast.success("Receptionist updated");
      }
      setOpen(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const triggerLabel = mode === "create"
    ? <><PlusCircle size={16} className="mr-1" /> New Receptionist</>
    : <><Pencil size={14} /></>;

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) reset(); }}>
      <DialogTrigger asChild>
        <Button size={mode === "create" ? "default" : "sm"}
                variant={mode === "create" ? "default" : "outline"}
                className={mode === "create" ? "bg-sky-500 hover:bg-sky-600 text-white" : ""}
                data-testid={mode === "create" ? "receptionist-new-btn" : `receptionist-edit-${initial?.id}`}>
          {triggerLabel}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{mode === "create" ? "New Receptionist" : "Edit Receptionist"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {mode === "create" && (
            <>
              <div>
                <Label>Custom ID (login username)</Label>
                <Input value={customId} onChange={(e) => setCustomId(e.target.value.trim())}
                       placeholder="e.g. rec_priya01" data-testid="receptionist-custom-id" />
                {customId && !validateCustomId(customId) ? (
                  <p className="text-rose-600 text-xs mt-1" data-testid="receptionist-custom-id-error">
                    {CUSTOM_ID_ERR}
                  </p>
                ) : (
                  <p className="text-[11px] text-slate-500 mt-1">3-20 characters · letters, numbers, or underscore. No email format.</p>
                )}
              </div>
              <div>
                <Label>Password</Label>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                       data-testid="receptionist-password" />
                {password && password.length < 8 ? (
                  <p className="text-rose-600 text-xs mt-1" data-testid="receptionist-password-error">
                    Password must be at least 8 characters
                  </p>
                ) : (
                  <p className="text-[11px] text-slate-500 mt-1">Minimum 8 characters.</p>
                )}
              </div>
            </>
          )}
          <div>
            <Label>Full name</Label>
            <Input value={fullName} onChange={(e) => setFullName(e.target.value)}
                   data-testid="receptionist-fullname" />
          </div>
          <div>
            <Label>Mobile</Label>
            <Input value={mobile} onChange={(e) => setMobile(e.target.value.replace(/[^\d+]/g, ""))}
                   maxLength={15} inputMode="tel" placeholder="e.g. 9812345678 or +919812345678"
                   data-testid="receptionist-mobile" />
            {mobile && !validateMobile(mobile) && (
              <p className="text-rose-600 text-xs mt-1" data-testid="receptionist-mobile-error">
                {MOBILE_ERR}
              </p>
            )}
          </div>
          <div>
            <Label>Permissions</Label>
            <PermissionCheckboxes value={permissions} onChange={setPermissions} />
          </div>
          <Button onClick={submit}
                  disabled={busy
                    || (mode === "create" && (!validateCustomId(customId) || password.length < 8))
                    || !validateMobile(mobile)}
                  className="w-full bg-emerald-500 hover:bg-emerald-600 text-white"
                  data-testid="receptionist-save-btn">
            {busy ? "Saving…" : (mode === "create" ? "Create Receptionist" : "Save Changes")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function ReceptionistsManagement() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [permsTarget, setPermsTarget] = useState(null); // receptionist row for modal

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/receptionist/list");
      setRows(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load receptionists");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const remove = async (r) => {
    try {
      await api.delete(`/receptionist/${r.id}`);
      toast.success(`Deactivated ${r.custom_id}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <Card className="p-6 space-y-4" data-testid="receptionists-management">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="rounded-full p-3 bg-indigo-100 text-indigo-700"><Users size={22} /></div>
          <div>
            <h3 className="font-heading text-xl font-semibold text-slate-900">Receptionist Accounts</h3>
            <p className="text-xs text-slate-500">Grant front-desk staff scoped access to your queues</p>
          </div>
        </div>
        <ReceptionistForm mode="create" onSaved={load} />
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Custom ID</TableHead>
            <TableHead>Full name</TableHead>
            <TableHead>Mobile</TableHead>
            <TableHead>Permissions</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            <TableRow><TableCell colSpan={6} className="text-center text-slate-400">Loading…</TableCell></TableRow>
          ) : rows.length === 0 ? (
            <TableRow><TableCell colSpan={6} className="text-center text-slate-400">No receptionists yet</TableCell></TableRow>
          ) : rows.map((r) => {
            const enabledCount = Object.entries(r.permissions || {})
              .filter(([k, v]) => v === true && k !== "other_features").length;
            return (
              <TableRow key={r.id} data-testid={`receptionist-row-${r.id}`}>
                <TableCell className="font-mono text-xs">{r.custom_id}</TableCell>
                <TableCell>{r.full_name}</TableCell>
                <TableCell>{r.mobile}</TableCell>
                <TableCell>
                  <Badge variant="secondary" className="text-[10px]" data-testid={`receptionist-perms-count-${r.id}`}>
                    <ShieldCheck size={10} className="mr-1" />
                    {enabledCount === 0 ? "none" : `${enabledCount} enabled`}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge variant={r.is_active ? "default" : "destructive"}>
                    {r.is_active ? "Active" : "Deactivated"}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button size="sm" variant="outline"
                            onClick={() => setPermsTarget(r)}
                            data-testid={`receptionist-permissions-${r.id}`}>
                      <ShieldCheck size={14} className="mr-1" /> Permissions
                    </Button>
                    <ReceptionistForm mode="edit" initial={r} onSaved={load} />
                    {r.is_active && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="ghost" className="text-rose-600 hover:text-rose-700"
                                  data-testid={`receptionist-delete-${r.id}`}>
                            <Trash2 size={14} />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Deactivate {r.custom_id}?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This soft-deletes the account. The receptionist will no longer be able to log in.
                              You can create a new account with the same custom ID later.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => remove(r)}
                                               className="bg-rose-600 hover:bg-rose-700 text-white"
                                               data-testid={`receptionist-delete-confirm-${r.id}`}>
                              Deactivate
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <PermissionsModal
        open={!!permsTarget}
        receptionist={permsTarget}
        onClose={() => setPermsTarget(null)}
        onSaved={() => { setPermsTarget(null); load(); }}
      />
    </Card>
  );
}
